import { theme } from "@lvt/material";
import { CameraViewers, CheckInResponse } from "@lvt/streaming";
import { unwrap } from "@lvt/web-data";
import { Typography, Toolbar } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import StarIcon from "@material-ui/icons/Star";
import StarBorderOutlinedIcon from "@material-ui/icons/StarBorderOutlined";
import { Skeleton } from "@material-ui/lab";

import { updateFavorites } from "@/api/users";
import HealthDot from "@/common/components/streaming/HealthDot/HealthDot";
import { RootState, useDispatch, useSelector } from "@/store";
import { useFullStory } from "@/utilities/hooks/fullstory";


const useStyles = makeStyles(() =>
    createStyles({
        root: {
            flexGrow: 1,
            paddingLeft: 0,
            paddingRight: 0,
            justifyContent: "space-between",
        },
        camName: {
            fontWeight: "bolder",
            marginLeft: "0.5em",
        },
        title: {
            flexGrow: 1,
        },
        camHeader: {
            display: "flex",
            alignItems: "center",
            paddingRight: "0.5em",
        },
        healthDot: {
            width: "min-content",
            lineHeight: 0,
        },
        placeholder: {
            width: "20em",
        },
        placeholderDot: {
            width: 16,
            height: 16,
            margin: 4,
            marginRight: 9,
        },
        tooltip: {
            fontSize: "1em",
        },
        iconBorder: {
            height: "4em",
            width: "4em",
            borderRadius: "50%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
        },
        cameraViewers: {
            justifyContent: "flex-end",
        },
    }),
);

interface StreamContentHeadProps {
    checkInData?: CheckInResponse;
}

const StreamContentHead: React.FC<StreamContentHeadProps> = ({ checkInData }) => {
    const FullStory = useFullStory();
    const muiClasses = useStyles();
    const streamingId = useSelector((state: RootState) => state.cameras.streamingId);
    const { user } = useSelector((state: RootState) => state);
    const streamMeta = useSelector((state: RootState) => unwrap(state.stream.streamMeta));
    const userLists = useSelector((state: RootState) => unwrap(state.userLists.userListsData));
    const dispatch = useDispatch();

    let isFavorite: boolean | null = null; //If favorite list is null, no cameras are fav

    if (userLists && userLists.favorites && streamingId) {
        isFavorite = userLists.favorites.cameras.some((favCamera) => favCamera.camId == streamingId);
    }

    const handleFavoriteClick = () => {
        if (user && user.id && streamingId) {
            if (isFavorite) {
                FullStory.event(FullStory.names.events.favoriteRemove, {
                    is_favorite: false,
                });
            } else {
                FullStory.event(FullStory.names.events.favoriteAdd, {
                    is_favorite: true,
                });
            }

            updateFavorites(!isFavorite, streamingId, user.id, userLists, dispatch);
        }
    };

    const buttonLabel = isFavorite ? "Unfavorite" : "Favorite";
    const unitHealthBackgroundColor = streamMeta && (streamMeta.cameraHealth.color === "#82C341" ? theme.palette.secondary.main : streamMeta.cameraHealth.color);

    return (
        <Toolbar disableGutters>
            <div className={muiClasses.camHeader}>
                {streamMeta ? (
                    <div className={muiClasses.iconBorder} style={{ backgroundColor: `${unitHealthBackgroundColor}16` }}>
                        <HealthDot
                            health={streamMeta.cameraHealth}
                            classes={muiClasses.healthDot}
                            size="2em"
                        />
                    </div>
                ) : (
                    <Skeleton variant="circle" className={muiClasses.placeholderDot}>
                        <HealthDot />
                    </Skeleton>
                )}
                <Typography className={muiClasses.camName} variant="h5">
                    {streamMeta ? (
                        streamMeta.cameraName
                    ) : (
                        <Skeleton variant="text" className={muiClasses.placeholder} />
                    )}
                </Typography>
            </div>
            {isFavorite !== null ? (
                <Tooltip
                    arrow={true}
                    title={buttonLabel}
                    placement="top"
                    classes={{ tooltip: muiClasses.tooltip }}
                    aria-label={buttonLabel}
                >
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={() => handleFavoriteClick()}
                        aria-label={buttonLabel}
                    >
                        {isFavorite ? (
                            <StarIcon color="secondary" fontSize="large" />
                        ) : (
                            <StarBorderOutlinedIcon color="secondary" fontSize="large" />
                        )}
                    </IconButton>
                </Tooltip>
            ) : (
                <Skeleton>
                    <StarBorderOutlinedIcon />
                </Skeleton>
            )}
            {checkInData && <CameraViewers className={muiClasses.cameraViewers} checkInData={checkInData} />}
        </Toolbar>
    );
};

export default StreamContentHead;
